import { z } from "zod";

/**
 * Central source of truth for all feature flags.
 * Add new flags here. Keys are stable identifiers stored in DB JSON.
 */
export const FEATURE_FLAGS = {
  UTILIZATION_TOOL: "utilization-tool",
  UTILIZATION_SETTINGS: "utilization-settings",
  EMERGENCY_OUTCOME: "emergency-outcome",
  SEND_SCHEDULER_BOOKING_TEXT: "send_scheduler_booking_text",
  CAMPAIGN_OPT_IN_GATE: "campaign-opt-in-gate",
  EXPOSE_CAMPAIGN_OPT_IN_SOURCE: "expose-campaign-out-in-source",
  SHOW_EXPORT_REPORTS: "show-export-reports",
  REQUIRE_MANAGER_ACCESS_FOR_REPORT_LOG: "require-manager-access-for-report-log",
  PAID_LEADS_FILTER: "paid-leads-filter",
  CXR_BOOKING: "cxr-booking",
  AUDIENCE_MEMBER_STATUS_FILTER: "audience-member-status-filter",
  DOUBLE_REACH_OUT_MESSAGE: "double-reach-out-message",
  SHOULD_AUTO_TRANSFER_AFTER_THRESHOLD: "should-auto-transfer-after-threshold",
  CAPACITY_SETTINGS_V2: "capacity-settings-v2",
  USE_HOLIDAY_SCHEDULE_OVERRIDES_NEW: "use-holiday-schedule-overrides-new",
  /** Include AI-generated call summaries in the customer notes list */
  INCLUDE_AI_NOTES_IN_CUSTOMER_NOTES: "include-ai-notes-in-customer-notes",
  /** Hide all emergency service UI from the dashboard (sidebar, hours settings, holiday settings) */
  HIDE_EMERGENCY_SERVICE: "hide-emergency-service",
  /** Hide the Analytics nav item from the sidebar */
  HIDE_ANALYTICS_NAV: "hide-analytics-nav",
  /** Hide the Campaigns nav item from the sidebar */
  HIDE_CAMPAIGNS_NAV: "hide-campaigns-nav",
  /** Enable the Call Scoring feature */
  ENABLE_CALL_SCORING: "enable-call-scoring",
  /** Enable the Interactions Reason Drilldown Sankey view */
  INTERACTIONS_REASON_DRILLDOWN_SANKEY: "interactions-reason-drilldown-sankey",
  /** Enable hub folder feature (sidebar folders, folder dropdown) */
  HUB_FOLDERS_ENABLED: "hub-folders-enabled",
  /** Enable the AHT (Average Handle Time) operational analytics dashboard */
  AHT_DASH: "aht-dash",
  /** Auto-flag all inbound text messages for human review (bypasses AI) */
  AUTO_FLAG_ALL_INBOUND_TEXTS: "auto-flag-all-inbound-texts",
} as const;

export type FeatureFlagKey = (typeof FEATURE_FLAGS)[keyof typeof FEATURE_FLAGS];

export type FeatureFlagMap = Record<FeatureFlagKey, boolean>;

// create a zod schema that should chnage with the FEATURE_FLAG object
const featureFlagDbShape: Record<FeatureFlagKey, z.ZodOptional<z.ZodBoolean>> = (
  Object.values(FEATURE_FLAGS) as FeatureFlagKey[]
).reduce(
  (acc, key) => {
    acc[key] = z.boolean().optional();
    return acc;
  },
  {} as Record<FeatureFlagKey, z.ZodOptional<z.ZodBoolean>>
);

export const featureFlagsDbSchema = z.object(featureFlagDbShape).partial();

/**
 * Parse raw DB value (unknown JSON) into a complete FeatureFlagMap, defaulting missing flags to false.
 */
export function parseDbFeatureFlags(raw: unknown): FeatureFlagMap {
  const parsed = featureFlagsDbSchema.catch({}).parse(raw ?? {});
  const allKeys = Object.values(FEATURE_FLAGS) as FeatureFlagKey[];
  const materialized: FeatureFlagMap = Object.fromEntries(
    allKeys.map(k => [k, Boolean(parsed[k] ?? false)])
  ) as FeatureFlagMap;
  return materialized;
}

/**
 * Validate a single flag key string.
 */
export function isKnownFeatureFlag(flag: string): flag is FeatureFlagKey {
  return (Object.values(FEATURE_FLAGS) as string[]).includes(flag);
}
